
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { FairClickSite, FairClickVisit, FairClickUserCredits } from '@/types/fairclick';
import { useToast } from '@/hooks/use-toast';
import { useToolUsageTracker } from '@/hooks/useToolUsageTracker';

export const useFairClick = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { trackToolUsage } = useToolUsageTracker();
  
  const [isLoading, setIsLoading] = useState(true);
  const [userSites, setUserSites] = useState<FairClickSite[]>([]);
  const [userCredits, setUserCredits] = useState<FairClickUserCredits | null>(null);
  const [currentSite, setCurrentSite] = useState<FairClickSite | null>(null);
  const [visitHistory, setVisitHistory] = useState<FairClickVisit[]>([]);
  const [visitLoading, setVisitLoading] = useState(false);
  
  // Fonction pour récupérer les sites de l'utilisateur
  const fetchUserSites = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('fairclick_sites')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      setUserSites(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des sites:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer vos sites',
        variant: 'destructive'
      });
    }
  }, [user?.id, toast]);
  
  // Fonction pour récupérer les crédits de l'utilisateur
  const fetchUserCredits = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('fairclick_user_credits')
        .select('*')
        .eq('user_id', user.id)
        .single();
        
      if (error && error.code !== 'PGRST116') { // PGRST116 = not found
        throw error;
      }
      
      if (data) {
        setUserCredits(data);
      } else {
        // Créer un enregistrement de crédit pour le nouvel utilisateur
        const { data: newCreditData, error: insertError } = await supabase
          .from('fairclick_user_credits')
          .insert({
            user_id: user.id,
            credits: 1, // Commencer avec 1 crédit gratuit
            total_visits_made: 0,
            total_visits_received: 0
          })
          .select()
          .single();
          
        if (insertError) throw insertError;
        
        setUserCredits(newCreditData);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des crédits:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer vos crédits',
        variant: 'destructive'
      });
    }
  }, [user?.id, toast]);
  
  // Fonction pour récupérer l'historique des visites
  const fetchVisitHistory = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('fairclick_visits')
        .select(`
          *,
          site:site_id (
            title,
            url
          )
        `)
        .eq('visitor_id', user.id)
        .order('visited_at', { ascending: false })
        .limit(50);
        
      if (error) throw error;
      
      setVisitHistory(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique des visites:', error);
    }
  }, [user?.id]);
  
  // Récupérer toutes les données au chargement
  const refreshData = useCallback(async () => {
    if (!user?.id) return;
    
    setIsLoading(true);
    try {
      await Promise.all([
        fetchUserSites(),
        fetchUserCredits(),
        fetchVisitHistory()
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, fetchUserSites, fetchUserCredits, fetchVisitHistory]);
  
  useEffect(() => {
    refreshData();
  }, [refreshData]);
  
  // Fonction pour obtenir un site aléatoire à visiter
  const getRandomSite = useCallback(async () => {
    if (!user?.id) return null;
    
    setVisitLoading(true);
    
    try {
      // Exclure les sites de l'utilisateur actuel pour éviter l'auto-visite
      const { data, error } = await supabase
        .from('fairclick_sites')
        .select('*')
        .neq('user_id', user.id)
        .gt('credits', 0) // Seulement les sites avec des crédits
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Sélectionner un site aléatoire parmi ceux disponibles
        const randomIndex = Math.floor(Math.random() * data.length);
        const site = data[randomIndex];
        setCurrentSite(site);
        return site;
      } else {
        setCurrentSite(null);
        return null;
      }
    } catch (error) {
      console.error('Erreur lors de la récupération d\'un site aléatoire:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de récupérer un site à visiter',
        variant: 'destructive'
      });
      setCurrentSite(null);
      return null;
    } finally {
      setVisitLoading(false);
    }
  }, [user?.id, toast]);
  
  // Fonction pour ajouter un site
  const addSite = useCallback(async (title: string, url: string, description: string, initialCredits: number) => {
    if (!user?.id) return;
    
    // Vérifier si l'utilisateur a assez de crédits
    if (userCredits && userCredits.credits < initialCredits) {
      toast({
        title: 'Crédits insuffisants',
        description: 'Vous n\'avez pas assez de crédits pour ajouter ce site',
        variant: 'destructive'
      });
      return;
    }
    
    // Ajouter le protocole si nécessaire
    let formattedUrl = url;
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }
    
    try {
      trackToolUsage('fairclick');
      
      // Créer le site
      const { data: siteData, error: siteError } = await supabase
        .from('fairclick_sites')
        .insert({
          user_id: user.id,
          title,
          url: formattedUrl,
          description,
          credits: initialCredits
        })
        .select()
        .single();
        
      if (siteError) throw siteError;
      
      // Déduire les crédits de l'utilisateur
      const { error: creditError } = await supabase
        .from('fairclick_user_credits')
        .update({
          credits: userCredits.credits - initialCredits
        })
        .eq('user_id', user.id);
        
      if (creditError) throw creditError;
      
      // Mettre à jour l'état local
      await refreshData();
      
      toast({
        title: 'Site ajouté',
        description: 'Votre site a été ajouté avec succès',
      });
      
      return siteData;
    } catch (error) {
      console.error('Erreur lors de l\'ajout du site:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'ajouter votre site',
        variant: 'destructive'
      });
    }
  }, [user?.id, userCredits, toast, refreshData, trackToolUsage]);
  
  // Fonction pour supprimer un site
  const deleteSite = useCallback(async (siteId: string) => {
    if (!user?.id) return;
    
    try {
      const { error } = await supabase
        .from('fairclick_sites')
        .delete()
        .eq('id', siteId)
        .eq('user_id', user.id); // Sécurité pour s'assurer que l'utilisateur possède ce site
        
      if (error) throw error;
      
      // Mettre à jour l'état local
      setUserSites(prev => prev.filter(site => site.id !== siteId));
      
      toast({
        title: 'Site supprimé',
        description: 'Votre site a été supprimé avec succès',
      });
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du site:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer votre site',
        variant: 'destructive'
      });
      return false;
    }
  }, [user?.id, toast]);
  
  // Fonction pour ajouter des crédits à un site
  const addCreditsToSite = useCallback(async (siteId: string, creditsToAdd: number) => {
    if (!user?.id || !userCredits) return;
    
    // Vérifier si l'utilisateur a assez de crédits
    if (userCredits.credits < creditsToAdd) {
      toast({
        title: 'Crédits insuffisants',
        description: 'Vous n\'avez pas assez de crédits',
        variant: 'destructive'
      });
      return false;
    }
    
    try {
      // Get current site info to update credits
      const { data: siteData, error: fetchError } = await supabase
        .from('fairclick_sites')
        .select('credits')
        .eq('id', siteId)
        .eq('user_id', user.id)
        .single();
        
      if (fetchError) throw fetchError;

      // Mettre à jour les crédits du site manuellement, sans utiliser RPC
      const { error: siteError } = await supabase
        .from('fairclick_sites')
        .update({
          credits: (siteData?.credits || 0) + creditsToAdd
        })
        .eq('id', siteId)
        .eq('user_id', user.id); // Sécurité
        
      if (siteError) throw siteError;
      
      // Déduire les crédits de l'utilisateur
      const { error: creditError } = await supabase
        .from('fairclick_user_credits')
        .update({
          credits: userCredits.credits - creditsToAdd
        })
        .eq('user_id', user.id);
        
      if (creditError) throw creditError;
      
      // Mettre à jour l'état local
      await refreshData();
      
      toast({
        title: 'Crédits ajoutés',
        description: `${creditsToAdd} crédit(s) ajouté(s) à votre site`,
      });
      
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'ajout de crédits:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'ajouter des crédits',
        variant: 'destructive'
      });
      return false;
    }
  }, [user?.id, userCredits, toast, refreshData]);
  
  // Fonction pour valider une visite
  const validateVisit = useCallback(async (siteId: string, duration: number) => {
    if (!user?.id) return false;
    
    try {
      trackToolUsage('fairclick');
      
      console.log(`Appel de validate_fairclick_visit avec visitor_id=${user.id}, site_id=${siteId}, duration=${duration}`);
      
      // Utiliser la fonction RPC pour valider la visite
      const { data, error } = await supabase
        .rpc('validate_fairclick_visit', { 
          visitor_id: user.id,
          site_id: siteId,
          visit_duration: duration
        });
        
      if (error) {
        console.error('Erreur RPC:', error);
        throw error;
      }
      
      console.log('Résultat de validate_fairclick_visit:', data);
      
      if (data) {
        // Rafraîchir les données après une visite validée
        await refreshData();
        
        toast({
          title: 'Visite validée',
          description: 'Vous avez gagné 1 crédit',
        });
        
        return true;
      } else {
        toast({
          title: 'Visite non validée',
          description: 'La visite n\'a pas pu être validée',
          variant: 'destructive'
        });
        
        return false;
      }
    } catch (error) {
      console.error('Erreur lors de la validation de la visite:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de valider la visite',
        variant: 'destructive'
      });
      return false;
    }
  }, [user?.id, toast, refreshData, trackToolUsage]);
  
  return {
    isLoading,
    userSites,
    userCredits,
    currentSite,
    visitHistory,
    visitLoading,
    getRandomSite,
    addSite,
    deleteSite,
    addCreditsToSite,
    validateVisit,
    refreshData
  };
};
